'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  expertise: string[];
  bio: string;
  achievements: string[];
}

interface TeamSectionProps {
  className?: string;
}

const TeamSection = ({ className = '' }: TeamSectionProps) => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: 'Vanessa Rodriguez',
    role: 'Founder & CEO',
    expertise: ['AI Strategy', 'Digital Transformation', 'Business Growth'],
    bio: 'With 15+ years in technology consulting, Vanessa founded CIS Solutions to democratize AI automation for businesses of all sizes. Her vision combines strategic thinking with practical implementation, helping companies navigate digital transformation with confidence.',
    achievements: [
    'Featured speaker at AI Summit 2025',
    'Published author: "AI for Business Leaders"',
    'Advisory board member, Tech Innovation Council']
  }];


  return (
    <section className={`py-20 lg:py-28 bg-card ${className}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
            Meet Our Leadership Team
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Visionary experts who combine strategic thinking with hands-on experience to drive your digital transformation success.
          </p>
        </div>

        <div className="grid md:grid-cols-1 lg:grid-cols-1 gap-8 mb-12 max-w-xs mx-auto">
          {teamMembers.map((member) =>
          <div
            key={member.id}
            className="bg-background rounded-xl overflow-hidden shadow-subtle hover:shadow-elevation transition-all duration-300 cursor-pointer group"
            onClick={() => setSelectedMember(member)}>

              <div className="relative h-32 overflow-hidden bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-3xl font-display font-bold text-primary">
                    {member.name.split(' ').map((n) => n[0]).join('')}
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                  <span className="text-primary-foreground font-semibold">View Profile</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-display font-semibold text-foreground mb-2">
                  {member.name}
                </h3>
                <p className="text-accent font-medium mb-4">{member.role}</p>
                <div className="flex flex-wrap gap-2">
                  {member.expertise.slice(0, 2).map((skill, idx) =>
                <span
                  key={idx}
                  className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full">

                      {skill}
                    </span>
                )}
                </div>
              </div>
            </div>
          )}
        </div>

        {selectedMember &&
        <div className="fixed inset-0 bg-authority/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedMember(null)}>
            <div className="bg-background rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="relative">
                <button
                onClick={() => setSelectedMember(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-background rounded-full flex items-center justify-center shadow-elevation hover:bg-muted transition-colors z-10"
                aria-label="Close profile">

                  <Icon name="XMarkIcon" size={24} className="text-foreground" />
                </button>
                
                <div className="p-8">
                  <div className="flex items-center gap-6 mb-8">
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-3xl font-display font-bold text-primary">
                        {selectedMember.name.split(' ').map((n) => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-3xl font-display font-bold text-foreground mb-2">
                        {selectedMember.name}
                      </h3>
                      <p className="text-xl text-accent font-semibold">{selectedMember.role}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-display font-semibold text-foreground mb-3">Expertise</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedMember.expertise.map((skill, idx) =>
                    <span
                      key={idx}
                      className="px-4 py-2 bg-primary/10 text-primary rounded-full">

                          {skill}
                        </span>
                    )}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-lg font-display font-semibold text-foreground mb-3">About</h4>
                    <p className="text-muted-foreground leading-relaxed">{selectedMember.bio}</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-display font-semibold text-foreground mb-3">Key Achievements</h4>
                    <ul className="space-y-2">
                      {selectedMember.achievements.map((achievement, idx) =>
                    <li key={idx} className="flex items-start space-x-2">
                          <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{achievement}</span>
                        </li>
                    )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </section>);

};

export default TeamSection;